import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MailQuestion, Send } from 'lucide-react';
import { FOLLOWUP_STATE_LABELS } from '@epasu/core/constants';
import type { FollowupState } from '@epasu/core/constants';
import { crm, type CampaignRow, type FollowupRow } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Select } from '@/components/ui/input';
import { PageHeader } from '@/components/PageHeader';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FollowupDialog } from '@/components/FollowupDialog';
import { FollowupSendDueDialog } from '@/components/FollowupSendDueDialog';
import { cn } from '@/lib/utils';
import { useUrlState } from '@/lib/useUrlState';
import { aktePfad } from '@/lib/contact-path';

/**
 * Freigaben: Erstansprachen nachfassen, von Hand oder für eine ganze
 * Kampagne auf einmal.
 *
 * Übernommen aus der früheren `FollowupsPage.tsx` (`/nachfassen`) — Task 6
 * ließ den Navigationspunkt bewusst stehen, weil ein halb umgezogener Punkt
 * sieben Tasks lang eine tote Route gewesen wäre. Neu ist der
 * Kampagnenfilter: Zeilen ohne Kampagne sind die von Hand geschriebenen
 * Erstansprachen und laufen unverändert weiter mit.
 */
type Reiter = 'laufend' | 'faellig' | 'beendet';

const REITER: { key: Reiter; label: string; states?: FollowupState[] }[] = [
  { key: 'laufend', label: 'Laufend' },
  { key: 'faellig', label: 'Fällig', states: ['faellig'] },
  {
    key: 'beendet',
    label: 'Abgeschlossen',
    states: ['beantwortet', 'kalt', 'gestoppt'],
  },
];

const LEERE_LISTE: Record<Reiter, string> = {
  laufend:
    'Keine laufende Erstansprache. Wer im Schreiben-Dialog das Häkchen setzt, landet hier.',
  faellig: 'Nichts fällig. Was drankommt, erscheint hier von selbst.',
  beendet:
    'Noch nichts abgeschlossen. Beantwortete, kalte und von Hand beendete Vorgänge stehen hier.',
};

/** Der Tag, an dem etwas überfällig wird — heute 0 Uhr. */
function heuteBeginn(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

/**
 * Fälligkeit, und zwar deutlich.
 *
 * Fälliges ist der Grund, warum jemand diese Seite aufmacht; überfälliges ist
 * der Grund, warum er es nicht früher getan hat. Beides wird hervorgehoben,
 * überfälliges stärker.
 */
function Faelligkeit({ dueAt }: { dueAt: string | null }) {
  if (!dueAt) return <span className="text-muted-foreground">—</span>;

  const datum = new Date(dueAt).toLocaleDateString('de-DE');
  const ueberfaellig = dueAt < heuteBeginn();
  const faellig = dueAt <= new Date().toISOString();

  if (ueberfaellig) {
    return <span className="font-semibold text-danger">{datum}</span>;
  }
  if (faellig) return <span className="font-semibold text-warning-foreground">{datum}</span>;
  return <span className="text-muted-foreground">{datum}</span>;
}

/** Welches Nachfassen als nächstes anstünde, und wie es um die Uhr steht. */
function Stufe({ zeile }: { zeile: FollowupRow }) {
  const zustand = zeile.state ? FOLLOWUP_STATE_LABELS[zeile.state] : null;

  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <span>
        {zeile.nextStep === null ? 'Alle Stufen ausgeschöpft' : `${zeile.nextStep}. Nachfass`}
      </span>
      {zustand && (
        <Badge
          variant={
            zeile.state === 'faellig'
              ? 'warning'
              : zeile.state === 'beantwortet'
                ? 'success'
                : zeile.state === 'offen'
                  ? 'info'
                  : 'neutral'
          }
        >
          {zustand}
        </Badge>
      )}
    </span>
  );
}

export function ApprovalsPage() {
  // Reiter und Kampagnenfilter stehen in der URL: wer in „Abgeschlossen“ einen
  // Lead oeffnet und zurueckgeht, soll wieder in „Abgeschlossen“ landen.
  // Der Parameter heißt `ansicht`, nicht `reiter`: `?reiter=` wählt den Reiter
  // der Akquise-Seite, in dem diese Arbeitsfläche steckt.
  const [reiter, setReiter] = useUrlState<Reiter>(
    'ansicht',
    'laufend',
    REITER.map((r) => r.key),
  );
  const [kampagnen, setKampagnen] = useState<CampaignRow[]>([]);
  const [campaignId, setCampaignId] = useUrlState<string>('kampagne', '');
  const [rows, setRows] = useState<FollowupRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);

  const [nachfassen, setNachfassen] = useState<FollowupRow | null>(null);
  const [beenden, setBeenden] = useState<FollowupRow | null>(null);
  const [beantwortet, setBeantwortet] = useState<FollowupRow | null>(null);
  const [wellenfassen, setWellenfassen] = useState(false);

  useEffect(() => {
    void crm.campaigns.list().then(setKampagnen);
  }, []);

  const load = useCallback(async () => {
    const gewaehlt = REITER.find((r) => r.key === reiter) ?? REITER[0]!;
    setFehler(null);
    try {
      setRows(
        await crm.followups.list({
          ...(gewaehlt.states ? { states: gewaehlt.states } : {}),
          ...(campaignId ? { campaignId } : {}),
        }),
      );
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Laden fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }, [reiter, campaignId]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  const gewaehlteKampagne = kampagnen.find((k) => k.id === campaignId);

  return (
    <div>
      <PageHeader>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1 border-b border-border">
            {REITER.map((r) => (
              <button
                key={r.key}
                onClick={() => setReiter(r.key)}
                className={cn(
                  '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                  reiter === r.key
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                {r.label}
              </button>
            ))}
          </div>

          <Select
            className="ml-auto h-9 w-56"
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}
            aria-label="Kampagne"
          >
            <option value="">Alle Kampagnen</option>
            {kampagnen.map((k) => (
              <option key={k.id} value={k.id}>
                {k.name}
              </option>
            ))}
          </Select>

          {gewaehlteKampagne && (
            <Button variant="outline" size="sm" onClick={() => setWellenfassen(true)}>
              <Send className="mr-1.5 h-4 w-4" />
              Alle fälligen dieser Kampagne senden
            </Button>
          )}
        </div>
      </PageHeader>

      {fehler && <p className="mb-3 text-sm text-danger">{fehler}</p>}

      {loading ? (
        <p className="text-sm text-muted-foreground">Lade …</p>
      ) : rows.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          <MailQuestion className="mx-auto mb-3 h-6 w-6" />
          {LEERE_LISTE[reiter]}
        </Card>
      ) : (
        <Card className="divide-y divide-border">
          {rows.map((z) => (
            <div
              key={z.messageId}
              className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm"
            >
              <span className="w-44 shrink-0 truncate">
                {z.contactId ? (
                  <Link
                    to={aktePfad(z.contactId, 'lead')}
                    className="text-brand hover:underline"
                  >
                    {z.contactName ?? z.contactEmail ?? 'Kontakt'}
                  </Link>
                ) : (
                  <span className="text-muted-foreground">
                    {z.contactEmail ?? 'Unbekannt'}
                  </span>
                )}
              </span>

              <span className="w-40 shrink-0 truncate text-muted-foreground">
                {z.organizationName ?? '—'}
              </span>

              <span
                className="min-w-0 flex-1 truncate"
                title={z.snippet ?? undefined}
              >
                {z.subject || '(ohne Betreff)'}
              </span>

              <span className="w-52 shrink-0">
                <Stufe zeile={z} />
              </span>

              <span className="w-24 shrink-0 text-right">
                <Faelligkeit dueAt={z.dueAt} />
              </span>

              <span className="flex shrink-0 gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={z.nextStep === null}
                  title={
                    z.nextStep === null
                      ? 'Alle Stufen sind ausgeschöpft — Ruhe ist eingekehrt.'
                      : undefined
                  }
                  onClick={() => setNachfassen(z)}
                >
                  Nachfassen
                </Button>
                {/*
                  „Beantwortet“ ist etwas anderes als „Beenden“: Letzteres
                  legt die Erstansprache als kalt ab. Wer von einer anderen
                  Adresse zurueckschreibt, hat aber geantwortet — das gehoert
                  in die Antwortquote und haelt die Kollegen derselben Firma an.
                */}
                {z.state === 'beantwortet' ? (
                  // Eine Abwesenheitsnotiz ist keine Antwort, und die
                  // Erkennung liegt manchmal daneben. Die Uhr laeuft danach ab
                  // der erreichten Stufe weiter, nicht wieder von vorn.
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!z.contactId}
                    onClick={async () => {
                      await crm.followups.reopen(z.contactId as string);
                      await load();
                    }}
                  >
                    Antwort zurücknehmen
                  </Button>
                ) : (
                  // Nur solange die Uhr laeuft. Auf einer kalten oder
                  // gestoppten Erstansprache haette der Knopf nichts
                  // anzuhalten und taete stillschweigend nichts.
                  (z.state === 'offen' || z.state === 'faellig') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={!z.contactId}
                      title={
                        z.contactId
                          ? undefined
                          : 'Diese Erstansprache hängt an keinem Kontakt.'
                      }
                      onClick={() => setBeantwortet(z)}
                    >
                      Antwort erhalten
                    </Button>
                  )
                )}
                <Button variant="ghost" size="sm" onClick={() => setBeenden(z)}>
                  Beenden
                </Button>
              </span>
            </div>
          ))}
        </Card>
      )}

      {!loading && rows.length > 0 && (
        <p className="mt-3 text-xs text-muted-foreground">
          {rows.length} {rows.length === 1 ? 'Erstansprache' : 'Erstansprachen'}
        </p>
      )}

      {nachfassen && (
        <FollowupDialog
          zeile={nachfassen}
          onClose={() => setNachfassen(null)}
          onSent={() => {
            setNachfassen(null);
            void load();
          }}
        />
      )}

      {beantwortet?.contactId && (
        <ConfirmDialog
          title="Antwort erhalten?"
          confirmLabel="Als beantwortet vermerken"
          onCancel={() => setBeantwortet(null)}
          onConfirm={async () => {
            await crm.followups.markAnswered(beantwortet.contactId as string);
            setBeantwortet(null);
            await load();
          }}
        >
          <p className="text-sm text-muted-foreground">
            Für {beantwortet.contactName ?? beantwortet.contactEmail ?? 'diesen Kontakt'}{' '}
            wird nicht mehr nachgefasst — auch nicht in anderen Kampagnen. Weitere
            Ansprechpartner derselben Firma werden in dieser Kampagne übersprungen,
            genau wie bei einer erkannten Antwort.
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Liegt die Antwort als Mail im Posteingang, ordne sie stattdessen dort
            dem Kontakt zu: das hält die Uhr genauso an und hängt die Nachricht an
            den Kontakt.
          </p>
        </ConfirmDialog>
      )}

      {beenden && (
        <ConfirmDialog
          title="Nachfassen beenden?"
          confirmLabel="Beenden"
          onCancel={() => setBeenden(null)}
          onConfirm={async () => {
            await crm.followups.stop(beenden.messageId);
            setBeenden(null);
            await load();
          }}
        >
          <p className="text-sm text-muted-foreground">
            Für {beenden.contactName ?? beenden.contactEmail ?? 'diesen Kontakt'} wird
            nicht mehr erinnert. Die Mail bleibt als Erstansprache vermerkt, die
            Uhr steht.
          </p>
        </ConfirmDialog>
      )}

      {wellenfassen && gewaehlteKampagne && (
        <FollowupSendDueDialog
          campaignId={gewaehlteKampagne.id}
          campaignName={gewaehlteKampagne.name}
          onClose={() => setWellenfassen(false)}
          onSent={() => void load()}
        />
      )}
    </div>
  );
}
